import styled from "styled-components";
import { useState, useContext, useEffect, createContext, useRef } from "react";
import { CurrentUserContext } from "./CurrentUserContext";
import { OrgTreeContext } from "./OrgTreeContext";

import {
  createNodesWithUsersFromUserInfo,
  getAllTeamIdsFromOrgTreeArray,
  iterateArrayOfNodesAndGiveParentsIfIndividualReporter,
} from "../Helpers/OrgTreeFunctions";
import { setupReportListeners, listenToReportDatabase } from "../Firebase";

interface ReportContext {
  reportData: ReportData[] | null;
  setNodesNeeded: React.Dispatch<React.SetStateAction<OrgTreeItem[] | null>>;
}

export const ReportDatabaseContext = createContext<ReportContext>({
  reportData: null,
  setNodesNeeded: () => {},
});

export default function ReportDatabaseContextProvider(props: Props) {
  const orgTree = useContext(OrgTreeContext);
  const currentUserContext = useContext(CurrentUserContext);
  const user = currentUserContext?.currentUser;
  const [reportData, setReportData] = useState<ReportData[] | null>(null);
  const [nodesNeeded, setNodesNeeded] = useState<OrgTreeItem[] | null>(null);
  const unsubscribeFunctions = useRef<(() => void)[]>([]);
  const [fetchTeamIds, setFetchTeamIds] = useState<string[] | null>(null);

  useEffect(() => {
    if (!nodesNeeded || !orgTree || !user) return;
    const nodeArrayWithParentsIfIndividual =
      iterateArrayOfNodesAndGiveParentsIfIndividualReporter(
        nodesNeeded,
        [orgTree],
        user
      );
    if (!nodeArrayWithParentsIfIndividual) return;
    const teamIdsFromUser = getAllTeamIdsFromOrgTreeArray(
      nodeArrayWithParentsIfIndividual
    );
    if (!teamIdsFromUser) return;
    function arraysAreEqual(arr1: any[] | null, arr2: any[] | null): boolean {
      if (!arr1 || !arr2) return false;
      return (
        arr1.length === arr2.length &&
        arr1.every((value, index) => value === arr2[index])
      );
    }
    if (!arraysAreEqual(teamIdsFromUser, fetchTeamIds)) {
      setFetchTeamIds(teamIdsFromUser);
    }
  }, [nodesNeeded]);

  useEffect(() => {
    if (!fetchTeamIds) return;
    const unsubscribeToReportDatabase = setupReportListeners(
      fetchTeamIds,
      (data) => {
        data && setReportData(data);
      }
    );
    console.log("reportData", reportData)
    unsubscribeFunctions.current.push(unsubscribeToReportDatabase);
  }, [fetchTeamIds]);

  useEffect(() => {
    // Cleanup: Unsubscribe from all listeners when the component unmounts
    return () => {
      unsubscribeFunctions.current.forEach((unsubscribe) => unsubscribe());
    };
  }, []);

  return (
    <ReportDatabaseContext.Provider value={{ reportData, setNodesNeeded }}>
      {props.children}
    </ReportDatabaseContext.Provider>
  );
}

interface Props {
  children: React.ReactNode;
}
